import { UntypedFormGroup } from '@angular/forms';
import omit from 'lodash-es/omit';

import { isNullOrUndefined } from '@app/util/util';
import { BookingArrangementVM, Travelers, PackageBookingPayload } from '@booking/entities/booking-arrangement';
import { BillingPayload, BillingResponse } from '@billing/entities/billing.payload';
import { createAt, payloadToCreate } from '@billing/mappers/billing-id.mapper';
import { BOOKING_FORM_INPUTS, TRAVELER_FORM_INPUTS } from '@booking/constants/forms.constants';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { paxByGuestRoomPackages, Room } from '@shared/base-search-bar/entities/room.entities';
import { formatTravelerDate } from '@booking/mappers/traveler-date.mapper';
import { PaxType } from '@booking/entities/pax-type';
import { AssistanceBookPayload } from '@shared/travel-assistance/entities/booking-assistance';
import { assistanceBookPayload } from '@booking/mappers/assistance-search-payload.mapper';
import { dobToDate } from '@ux/mappers/date.mapper';

export function packageBookingPayloadMapper(
  form: UntypedFormGroup,
  validateOptionIdAir: string,
  validateOptionIdHotel: string,
  arrangement: BookingArrangementVM,
  billingInfo: BillingResponse,
  rooms: Room[],
  assistanceInfo: { optionId: number, searchId: string }
): PackageBookingPayload {
  const travelers = getTravelers(arrangement, rooms);
  const createIsEnabledByBilling = createAt(billingInfo);
  const createIsEnabledByAssistance = !isNullOrUndefined(assistanceInfo?.optionId); return {
    arrangement: {
      AdditionalInformation: form.value.Comments,
      ValidateOptionIdAir: validateOptionIdAir,
      ValidateOptionIdHotel: validateOptionIdHotel,
      TravelItineraryId: 0,
      Travelers: travelers
    },
    billing: {
      createIsEnabled: createIsEnabledByBilling,
      payload: createIsEnabledByBilling ? payloadToCreate(billingInfo, arrangement) : {} as BillingPayload
    },
    assistance: {
      createIsEnabled: createIsEnabledByAssistance,
      payload: createIsEnabledByAssistance ?
        assistanceBookPayload(assistanceInfo?.searchId, travelers,
          assistanceInfo?.optionId) : {} as AssistanceBookPayload
    },
  };
}

function formatTraveler(traveler: Travelers): Travelers {
  const travelerName = `${traveler[TRAVELER_FORM_INPUTS.FIRST_NAME]} ${traveler[TRAVELER_FORM_INPUTS.SECOND_NAME]}`.trim();
  return omit({ ...traveler, [TRAVELER_FORM_INPUTS.FIRST_NAME]: travelerName },
    [
      TRAVELER_FORM_INPUTS.SECOND_NAME,
      TRAVELER_FORM_INPUTS.USER_ID,
      TRAVELER_FORM_INPUTS.INTERNATIONAL_DOCUMENT_REQUIRED
    ]);
}

function getTravelers(arrangement: BookingArrangementVM, rooms: Room[]): Travelers[] {
  const travelers = arrangement[BOOKING_FORM_INPUTS.TRAVELERS].map(traveler => ({
    ...formatTraveler(traveler),
    DOB: formatTravelerDate(dobToDate(traveler.DOB)),
    Phone: arrangement.Phone,
    Email: arrangement.Email
  }));

  let _travelers = travelers as Travelers[];

  rooms.forEach((room, roomIndex) => {
    const roomNumber = roomIndex + 1;
    _travelers = getTravelersWithRoomNumber(_travelers, room, roomNumber);
  });

  return _travelers.filter(traveler => !isNullOrUndefined(traveler.RoomNumber));
}


function getTravelersWithRoomNumber(travelers: Travelers[], room: Room, roomNumber: number): Travelers[] {
  let adults = room[TravelerTypes.ADULTS];
  let children = room[TravelerTypes.CHILDREN];
  return travelers.map(traveler => {
    const type = paxByGuestRoomPackages[traveler.PaxType];
    if (isNullOrUndefined(traveler.RoomNumber)) {
      switch (type) {
        case TravelerTypes.CHILDREN:
        case TravelerTypes.INFANTS:
          if (children > 0) {
            children--;
            return {
              ...traveler,
              PaxType: PaxType.CHILDREN,
              RoomNumber: roomNumber
            };
          } else return traveler;
        default:
          if (adults > 0) {
            adults--;
            return {
              ...traveler,
              RoomNumber: roomNumber
            };
          } else return traveler;
      }
    } else return traveler;
  });
}
